import { Button, Icon, PopUp, theme } from '@ketch-com/deck'
import { Box, styled, Typography } from '@mui/material'
import { HistoricalConsentContent } from 'pages/insights/auditLog/components/historicalPermitPopup/components/HistoricalConsentContent'
import { HistoricalSubscriptionsContent } from 'pages/insights/auditLog/components/historicalPermitPopup/components/HistoricalSubscriptionsContent'
import { HistoricalPermitPopupVariant } from 'pages/insights/auditLog/components/historicalPermitPopup/constants'
import { useHistoricalPermits } from 'pages/insights/auditLog/components/historicalPermitPopup/useHistoricalPermits'
import { getHistoricalPermitPopupTitle } from 'pages/insights/auditLog/components/historicalPermitPopup/utils'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import React, { useCallback, useEffect } from 'react'

export type HistoricalPermitPopupProps = {
  variant: HistoricalPermitPopupVariant
}

const LoadingBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  width: '912px',
  height: '478px',
  borderRadius: '11px',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  background: theme.palette.Black.o8,
})

export const HistoricalPermitPopup: React.FC<HistoricalPermitPopupProps> = ({ variant }) => {
  const { setAuditLogHistoricalPermitPopupVariant, queries } = useAuditLogContext()

  const { permitHistory, isReady, refetchPoll } = useHistoricalPermits(variant)
  const isConsent = variant === HistoricalPermitPopupVariant.Consent

  // Poll the permit history every 3 seconds until it's ready
  useEffect(() => {
    let intervalId: NodeJS.Timeout
    if (!isReady) {
      intervalId = setInterval(() => {
        refetchPoll()
      }, 3000)
    }

    // Clear the interval when component unmounts or status changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isReady, refetchPoll])

  const closePopup = useCallback(() => {
    setAuditLogHistoricalPermitPopupVariant(null)
  }, [setAuditLogHistoricalPermitPopupVariant])

  return (
    <PopUp
      title={getHistoricalPermitPopupTitle(variant, queries.identifierSearch || 'N/A')}
      variant="modal"
      onClose={closePopup}
      popUpActionChildren={
        <Button size="large" color="secondary" onClick={closePopup}>
          Close
        </Button>
      }
    >
      {isReady ? (
        isConsent ? (
          <HistoricalConsentContent permitHistory={permitHistory} />
        ) : (
          <HistoricalSubscriptionsContent permitHistory={permitHistory} />
        )
      ) : (
        <LoadingBox>
          <Icon name="OClock" width={48} height={48} />
          <Typography variant="h4">Please Wait</Typography>
          <Typography>
            We're loading all historical consent records for "{queries.identifierSearch || 'N/A'}".
            <br />
            This might take a minute. You may close this window and return later.
          </Typography>
        </LoadingBox>
      )}
    </PopUp>
  )
}
